import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PhoneIcon from "@mui/icons-material/Phone";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DeliveryImagePreviewDialog, {
  DeliveryImagePreviewDetail,
} from "../../components/dialogs/DeliveryImagePreviewDialog";
import NavigationBar from "../../components/global/Layout/MainLayout/NavigationBar";
import { SearchInput } from "../../components/global/Input";
import { useForm, Controller } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { useQueries } from "react-query";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/th";

// Hooks
import { useGetAdminDeliverySheets } from "../../hook/query/useAdminDeliverySheet";

// API
import { APIGetAdminDeliverySheetDetail } from "../../service/api/APIAdminDeliverySheet";
import { multiFranchiseAxios } from "../../service/axios/multiFranchiseAxios";

// Types
import {
  AdminDeliverySheetDetail,
  AdminDeliverySheetItem,
  AdminDeliverySheetResponse,
  getDeliveryStatusInfo,
} from "../../compiler/deliverySheet.interface";
import { formatBangkokDateTime } from "../../utilities/bangkokDateTime";
import { getDeliveryPhotoLocation } from "../../utilities/deliveryPhotoAudit";
import EQueryKeys from "../../utilitie/queryKey";
import {
  getDeliveryDropRenderId,
  getLatestDeliveryTimelineValue,
  groupDeliverySheetRows,
  resolveDeliveryTimelineTimes,
} from "./deliveryTimelineTimes";

// Types for internal use
interface FormValues {
  keyword: string;
  delivery_date: Dayjs;
}

interface RouteGroup {
  id: string;
  routeNo: number;
  name: string;
  route_code: string;
  driver: string;
  phone: string;
  drops: DropPoint[];
}

interface DropPoint {
  id: string;
  uuid: string;
  orderUuid?: string;
  detailTargets: Array<{ uuid: string; orderUuid?: string }>;
  routeCode: string;
  driver: string;
  driverPhone: string;
  plannedTime: string;
  latestTime: string;
  actualTime: string | null;
  effectiveTime: string;
  comparisonLabel: string | null;
  delayMinutes: number | null;
  dropNo: string;
  branchCode: string;
  dropName: string;
  branchName: string;
  status: string;
  orderCode: string;
  totalAmount: number;
  itemCount: number;
  shippingType: string;
  deliveryAddress?: string;
  invoiceNumber?: string;
  invoiceStatus?: string;
  notes?: string;
}

const TIMELINE_START_HOUR = 6;
const TIMELINE_END_HOUR = 23;
const TIMELINE_START_MINUTES = TIMELINE_START_HOUR * 60;
const TIMELINE_END_MINUTES = TIMELINE_END_HOUR * 60;
const STORE_CLOSING_HOUR = 18;
const STORE_CLOSING_MINUTES = STORE_CLOSING_HOUR * 60;
const LATE_RISK_WARNING_MINUTES = 30;
const TIMELINE_AXIS_OFFSET_PX = 75;
const TIMELINE_TRAILING_SPACE_PX = 200;
const TIMELINE_RANGE_MINUTES = TIMELINE_END_MINUTES - TIMELINE_START_MINUTES;
const BASE_TIMELINE_WIDTH = TIMELINE_RANGE_MINUTES * 5;
const MIN_TIMELINE_ZOOM = 0.35;
const MAX_TIMELINE_ZOOM = 1.4;
const TIMELINE_ZOOM_STEP = 0.15;
type TimelineDensity = "overview" | "compact" | "comfortable";
type DropPlanRiskLevel = "warning" | "late";
type DeliveryPhoto = AdminDeliverySheetDetail["delivery_info"]["delivery_photos"][number];

interface DeliveryPhotoPreviewState {
  photo: DeliveryPhoto;
  index: number;
  detail: AdminDeliverySheetDetail;
}

interface DropPlanRisk {
  level: DropPlanRiskLevel;
  label: string;
  shortLabel: string;
  helperText: string;
  cardBg: string;
  borderColor: string;
  textColor: string;
  chipBg: string;
}

interface RouteProductSummary {
  productName: string;
  quantity: number;
  dropCount: number;
}

interface RouteStatusSummary {
  label: string;
  count: number;
  bgColor: string;
  textColor: string;
}

// สร้าง timeSlots ตั้งแต่ 6:00 - 23:00 ทุก 30 นาที เพื่อให้เห็นงานหลังเวลาปิดร้าน
const timeSlots = Array.from({ length: TIMELINE_RANGE_MINUTES / 30 + 1 }, (_, i) => {
  const h = Math.floor(i / 2) + TIMELINE_START_HOUR;
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(1, "0")}:${m}`;
});

// Mock route + drop point
// const routes = [
//   {
//     id: 1,
//     name: "#1 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:00", label: "ลาดพร้าว", status: "normal" },
//       { time: "07:45", label: "เพชรเกษม", status: "warning" },
//       { time: "09:00", label: "พระราม 2", status: "fail" },
//       { time: "12:30", label: "อ่อนนุช", status: "normal" },
//       { time: "14:15", label: "ปทุมวัน", status: "normal" },
//     ],
//   },
//   {
//     id: 2,
//     name: "#2 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:45", label: "พุทธมณฑลสาย 3", status: "warning" },
//       { time: "08:00", label: "เพชรเกษม", status: "fail" },
//       { time: "10:10", label: "พุทธมณฑลสาย 1", status: "normal" },
//       { time: "13:00", label: "รามคำแหง", status: "normal" },
//       { time: "17:40", label: "อ่อนนุช", status: "fail" },
//     ],
//   },
//   {
//     id: 3,
//     name: "#3 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:30", label: "คลองเตย", status: "normal" },
//       { time: "08:45", label: "ลาดพร้าว", status: "warning" },
//       { time: "11:20", label: "ปทุมวัน", status: "normal" },
//       { time: "14:00", label: "พุทธมณฑลสาย 2", status: "fail" },
//       { time: "16:30", label: "พระราม 2", status: "warning" },
//     ],
//   },
//   {
//     id: 4,
//     name: "#4 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "07:00", label: "พุทธมณฑลสาย 1", status: "fail" },
//       { time: "09:20", label: "เพชรเกษม", status: "normal" },
//       { time: "11:00", label: "อ่อนนุช", status: "normal" },
//       { time: "13:30", label: "ลาดพร้าว", status: "warning" },
//       { time: "15:50", label: "พุทธมณฑลสาย 3", status: "fail" },
//     ],
//   },
//   {
//     id: 5,
//     name: "#5 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:20", label: "พระราม 2", status: "warning" },
//       { time: "08:00", label: "ปทุมวัน", status: "normal" },
//       { time: "10:30", label: "คลองเตย", status: "fail" },
//       { time: "13:45", label: "พุทธมณฑลสาย 2", status: "normal" },
//       { time: "16:00", label: "ลาดพร้าว", status: "normal" },
//     ],
//   },
//   {
//     id: 6,
//     name: "#6 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "07:10", label: "เพชรเกษม", status: "normal" },
//       { time: "08:30", label: "อ่อนนุช", status: "fail" },
//       { time: "10:45", label: "พุทธมณฑลสาย 1", status: "warning" },
//       { time: "14:30", label: "พระราม 2", status: "normal" },
//       { time: "17:10", label: "รามคำแหง", status: "fail" },
//     ],
//   },
//   {
//     id: 7,
//     name: "#7 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:55", label: "พุทธมณฑลสาย 2", status: "normal" },
//       { time: "09:00", label: "รามคำแหง", status: "normal" },
//       { time: "12:15", label: "พุทธมณฑลสาย 3", status: "fail" },
//       { time: "15:20", label: "คลองเตย", status: "warning" },
//       { time: "17:55", label: "ปทุมวัน", status: "normal" },
//     ],
//   },
//   {
//     id: 8,
//     name: "#8 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "07:20", label: "ลาดพร้าว", status: "fail" },
//       { time: "09:45", label: "อ่อนนุช", status: "normal" },
//       { time: "11:30", label: "พุทธมณฑลสาย 1", status: "warning" },
//       { time: "14:50", label: "รามคำแหง", status: "normal" },
//       { time: "16:40", label: "เพชรเกษม", status: "fail" },
//     ],
//   },
//   {
//     id: 9,
//     name: "#9 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "06:35", label: "พระราม 2", status: "normal" },
//       { time: "08:50", label: "ปทุมวัน", status: "warning" },
//       { time: "11:15", label: "พุทธมณฑลสาย 2", status: "normal" },
//       { time: "13:25", label: "คลองเตย", status: "fail" },
//       { time: "16:20", label: "พุทธมณฑลสาย 3", status: "normal" },
//     ],
//   },
//   {
//     id: 10,
//     name: "#10 - 50 Drop",
//     driver: "เอกพงศ์ ซอบจับรอ",
//     phone: "097-444-5555",
//     drops: [
//       { time: "07:40", label: "พุทธมณฑลสาย 1", status: "fail" },
//       { time: "09:30", label: "ลาดพร้าว", status: "normal" },
//       { time: "12:00", label: "พระราม 2", status: "warning" },
//       { time: "15:10", label: "อ่อนนุช", status: "normal" },
//       { time: "17:30", label: "รามคำแหง", status: "fail" },
//     ],
//   },
// ];

// Mock route + drop point (ตัวอย่าง 18 routes, จุดส่ง 30-40 จุด/route)
const routes = Array.from({ length: 18 }, (_, routeIdx) => ({
  id: routeIdx + 1,
  routeNo: routeIdx + 1,
  name: `#${routeIdx + 1}`,
  driver: "เอกพงศ์ ซอบจับรอ",
  phone: "097-444-5555",
  drops: Array.from({ length: 30 + (routeIdx % 11) }, (_, dropIdx) => {
    const hour = 6 + Math.floor((dropIdx * 12) / (30 + (routeIdx % 11)));
    const minute = Math.floor(Math.random() * 59);
    const time = `${hour.toString().padStart(2, "0")}:${minute
      .toString()
      .padStart(2, "0")}`;
    const statusArr = ["normal", "warning", "fail"];
    const status = statusArr[(dropIdx + routeIdx) % 3];
    return {
      id: `drop-${routeIdx + 1}-${dropIdx + 1}`,
      sendFlag: true,
      recieveFlag: dropIdx % 2 === 0,
      time,
      dropNo: `PE${30 + dropIdx}`,
      dropName: `จุดที่ ${dropIdx + 1}`,
      duration: "20",
      orders: 1 + (dropIdx % 4),
      delivered: dropIdx % 3 !== 0,
      captured: dropIdx % 4 === 0,
      retured: dropIdx % 10 === 0,
      proofOfDelivery: dropIdx % 5 !== 0,
      nameProof: dropIdx % 5 === 0 ? "รับสินค้า มีสกุล" : "",
      photoProofUrls: [],
      note: dropIdx % 7 === 0 ? "หมายเหตุ" : "",
      status,
      latitude: "",
      longitude: "",
      checkInAt: time,
      updatedAt: time,
      completedAt: time,
    };
  }),
}));

const clampTimelineZoom = (value: number) =>
  Math.min(MAX_TIMELINE_ZOOM, Math.max(MIN_TIMELINE_ZOOM, value));

const getTimelineDensity = (timelineZoom: number): TimelineDensity => {
  if (timelineZoom <= 0.45) return "overview";
  if (timelineZoom <= 0.7) return "compact";
  return "comfortable";
};

const getDropCardMetrics = (timelineZoom: number) => {
  const density = getTimelineDensity(timelineZoom);
  if (density === "overview") return { density, width: 72, height: 58 };
  if (density === "compact") return { density, width: 112, height: 72 };
  return { density, width: 164, height: 86 };
};

const getTimelineRowHeight = (timelineZoom: number) => {
  const density = getTimelineDensity(timelineZoom);
  if (density === "overview") return 72;
  if (density === "compact") return 86;
  return 98;
};

const getMinutesFromTime = (time: string): number | null => {
  const [rawHour, rawMinute] = time.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const getTimelineLeftPxForMinutes = (minutes: number, timelineWidth: number): number => {
  const percent = Math.min(
    1,
    Math.max(0, (minutes - TIMELINE_START_MINUTES) / TIMELINE_RANGE_MINUTES)
  );
  return percent * timelineWidth + TIMELINE_AXIS_OFFSET_PX;
};

const getTimelineContentWidth = (timelineWidth: number): number =>
  timelineWidth + TIMELINE_AXIS_OFFSET_PX + TIMELINE_TRAILING_SPACE_PX;

const getOrderSuffix = (orderCode?: string, fallback?: string): string =>
  orderCode?.split("-")[2] || fallback || "-";

const getBranchCodeFromItem = (item: AdminDeliverySheetItem, fallback?: string): string => {
  const itemWithBranchInfo = item as AdminDeliverySheetItem & {
    branch_info?: { branch_code?: string };
  };

  return item.branch_code || itemWithBranchInfo.branch_info?.branch_code || getOrderSuffix(item.order_code, fallback);
};

const getDropStatusInfo = (status: string | number, shippingType?: string) => {
  const baseInfo = getDeliveryStatusInfo(status, shippingType);
  const normalized = String(status || "").toLowerCase();

  if (normalized === "in_transit" || normalized === "in_progress") {
    return {
      ...baseInfo,
      label: "กำลังส่ง",
      shortLabel: "ส่งอยู่",
      bgColor: "#e8f2ff",
      textColor: "#15559a",
      borderColor: "#7fb3e8",
      cardBg: "#f5f9ff",
    };
  }

  if (normalized === "delivered" || status === 2) {
    return {
      ...baseInfo,
      shortLabel: "สำเร็จ",
      borderColor: "#7fc47d",
      cardBg: "#dcf5d5",
    };
  }

  if (normalized === "assigned" || status === 1) {
    return {
      ...baseInfo,
      shortLabel: "รอส่ง",
      borderColor: "#f2b63d",
      cardBg: "#fff2c8",
    };
  }

  if (normalized === "cancelled") {
    return {
      ...baseInfo,
      shortLabel: "ยกเลิก",
      borderColor: "#ef6f6c",
      cardBg: "#fff0ef",
    };
  }

  return {
    ...baseInfo,
    shortLabel: baseInfo.label || "รอ",
    borderColor: "#b8b8b8",
    cardBg: "#f7f7f7",
  };
};

const getDropPlanRisk = (drop: DropPoint): DropPlanRisk | null => {
  const etaMinutes = getMinutesFromTime(drop.effectiveTime);
  if (etaMinutes === null) return null;

  const normalizedStatus = String(drop.status || "").toLowerCase();
  if (normalizedStatus === "cancelled") return null;

  const isDelivered = normalizedStatus === "delivered" || drop.status === 2;
  const referenceLabel = drop.actualTime ? "เวลาส่งจริง" : "ETA ล่าสุด";

  if (etaMinutes >= STORE_CLOSING_MINUTES) {
    return {
      level: "late",
      label: isDelivered ? "ส่งหลังปิดร้าน" : "ตกแผน",
      shortLabel: isDelivered ? "หลังปิด" : "ตกแผน",
      helperText: isDelivered
        ? `${referenceLabel}อยู่หลัง 18:00`
        : `${referenceLabel}อยู่หลัง 18:00 มีโอกาสส่งไม่ทันเวลาปิดร้าน`,
      cardBg: "#fff1f2",
      borderColor: "#d32f2f",
      textColor: "#b42318",
      chipBg: "#ffe4e6",
    };
  }

  if (!isDelivered && etaMinutes >= STORE_CLOSING_MINUTES - LATE_RISK_WARNING_MINUTES) {
    return {
      level: "warning",
      label: "เสี่ยงส่งไม่ทัน",
      shortLabel: "เสี่ยง",
      helperText: `${referenceLabel}อยู่ในช่วง 30 นาทีก่อนปิดร้าน`,
      cardBg: "#fff7e6",
      borderColor: "#f59e0b",
      textColor: "#92400e",
      chipBg: "#ffedd5",
    };
  }

  return null;
};

const getDropTimeSummary = (drop: DropPoint): string => {
  const parts = [`แผน ${drop.plannedTime}`];
  if (drop.comparisonLabel) parts.push(drop.comparisonLabel);
  if (drop.delayMinutes !== null && drop.comparisonLabel) {
    if (drop.delayMinutes > 0) parts.push(`ช้า ${drop.delayMinutes} นาที`);
    if (drop.delayMinutes < 0) parts.push(`เร็ว ${Math.abs(drop.delayMinutes)} นาที`);
    if (drop.delayMinutes === 0) parts.push("ตรงตามแผน");
  }
  return parts.join(" • ");
};

const getDropDelayChip = (drop: DropPoint) => {
  if (drop.delayMinutes === null || !drop.comparisonLabel) return null;
  if (drop.delayMinutes > 0) {
    return {
      shortLabel: `+${drop.delayMinutes}น`,
      longLabel: `ช้า ${drop.delayMinutes}น`,
      bgColor: "#ffedd5",
      textColor: "#92400e",
    };
  }
  if (drop.delayMinutes < 0) {
    return {
      shortLabel: `-${Math.abs(drop.delayMinutes)}น`,
      longLabel: `เร็ว ${Math.abs(drop.delayMinutes)}น`,
      bgColor: "#dcfce7",
      textColor: "#166534",
    };
  }
  return {
    shortLabel: "ตรงแผน",
    longLabel: "ตรงตามแผน",
    bgColor: "#dcfce7",
    textColor: "#166534",
  };
};

const DropCard = ({
  drop,
  timelineWidth,
  timelineZoom,
  onSelect,
}: {
  drop: DropPoint;
  timelineWidth: number;
  timelineZoom: number;
  onSelect: (drop: DropPoint) => void;
}) => {
  const { density, width: cardWidth, height: cardHeight } = getDropCardMetrics(timelineZoom);
  const statusInfo = getDropStatusInfo(drop.status, drop.shippingType);
  const planRisk = getDropPlanRisk(drop);
  const delayChip = getDropDelayChip(drop);
  const showBranch = density !== "overview";
  const showMeta = density === "comfortable";
  const tooltipTitle = `${drop.branchName} • ${drop.orderCode} • ${getDropTimeSummary(drop)} • ${
    planRisk ? `${planRisk.label}: ${planRisk.helperText}` : statusInfo.label
  }`;
  const cardBg = planRisk?.cardBg || statusInfo.cardBg;
  const borderColor = planRisk?.borderColor || statusInfo.borderColor;
  const statusChipLabel =
    planRisk?.shortLabel ||
    (showMeta ? delayChip?.longLabel : delayChip?.shortLabel) ||
    statusInfo.shortLabel;
  const statusChipBg = planRisk?.chipBg || delayChip?.bgColor || statusInfo.bgColor;
  const statusChipColor = planRisk?.textColor || delayChip?.textColor || statusInfo.textColor;

  return (
    <Tooltip title={tooltipTitle} enterDelay={250} arrow>
      <Paper
        role="button"
        tabIndex={0}
        onClick={() => onSelect(drop)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(drop);
          }
        }}
        sx={{
          p: density === "overview" ? 0.55 : 0.75,
          width: cardWidth,
          height: cardHeight,
          overflow: "hidden",
          position: "absolute",
          top: 0,
          left: getDropCardLeftOffsetFromTime(drop.plannedTime, timelineWidth, cardWidth),
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 1,
          boxShadow: planRisk
            ? `0 0 0 1px ${borderColor}22, 0 5px 12px rgba(15, 23, 42, 0.16)`
            : "0 1px 2px rgba(15, 23, 42, 0.12)",
          zIndex: 10,
          cursor: "pointer",
          transition: "box-shadow 140ms ease, transform 140ms ease, border-color 140ms ease",
          "&:hover": {
            boxShadow: "0 6px 14px rgba(15, 23, 42, 0.18)",
            transform: "translateY(-1px)",
            zIndex: 20,
          },
          "&:focus-visible": {
            outline: "2px solid #15803d",
            outlineOffset: 2,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: 3,
            height: "100%",
            bgcolor: borderColor,
            borderTopLeftRadius: 6,
            borderBottomLeftRadius: 6,
          },
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              minWidth: density === "overview" ? 42 : 50,
              height: density === "overview" ? 18 : 20,
              px: 0.75,
              borderRadius: "999px",
              bgcolor: "#e7ff2f",
              border: "1px solid #35a852",
              color: "#17240e",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: density === "overview" ? 10 : 11,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {drop.branchCode}
          </Box>
          {showBranch && (
            <Typography
              fontWeight={800}
              fontSize={density === "compact" ? 11 : 12}
              noWrap
              title={drop.branchName}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {drop.branchName}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          mt={density === "overview" ? 0.45 : 0.6}
          sx={{ minWidth: 0 }}
        >
          <AccessTimeIcon sx={{ fontSize: 12, color: "#374151" }} />
          <Typography
            variant="caption"
            sx={{ fontSize: density === "overview" ? 10 : 11, fontWeight: 700 }}
          >
            {density === "overview" ? drop.plannedTime : `แผน ${drop.plannedTime}`}
          </Typography>
          {showMeta && (
            <>
              <ReceiptLongIcon sx={{ fontSize: 12, color: "#374151" }} />
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700 }}>
                {drop.itemCount || 0}
              </Typography>
            </>
          )}
        </Stack>

        {density === "overview" ? (
          <Box
            sx={{
              mt: 0.45,
              height: 5,
              width: "100%",
              borderRadius: "999px",
              bgcolor: borderColor,
              opacity: 0.85,
            }}
          />
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.45}>
            <Typography
              variant="caption"
              noWrap
              sx={{ fontSize: 10.5, color: planRisk?.textColor || "#374151", minWidth: 0 }}
            >
              {showMeta
                ? drop.comparisonLabel ||
                  planRisk?.label ||
                  (drop.shippingType === "pickup" ? "รับของ" : "จัดส่ง")
                : statusChipLabel}
            </Typography>
            <Chip
              label={statusChipLabel}
              size="small"
              sx={{
                height: 18,
                maxWidth: showMeta ? 72 : 54,
                fontSize: 9.5,
                fontWeight: 700,
                bgcolor: statusChipBg,
                color: statusChipColor,
              }}
            />
          </Stack>
        )}
      </Paper>
    </Tooltip>
  );
};

function getStoreClosingLeftPx(timelineWidth: number) {
  return getTimelineLeftPxForMinutes(STORE_CLOSING_MINUTES, timelineWidth);
}

function getDropCardLeftOffsetFromTime(
  time: string,
  timelineWidth: number,
  cardWidth: number
): string {
  const totalMinutes = getMinutesFromTime(time) ?? TIMELINE_START_MINUTES;
  const etaAnchorLeft = getTimelineLeftPxForMinutes(totalMinutes, timelineWidth);
  const maxLeft = getTimelineContentWidth(timelineWidth) - cardWidth;
  return `${Math.min(maxLeft, Math.max(0, etaAnchorLeft)).toFixed(0)}px`;
}

function StoreClosingRiskZone({ timelineWidth }: { timelineWidth: number }) {
  const left = getStoreClosingLeftPx(timelineWidth);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: `${left}px`,
        right: 0,
        bgcolor: "rgba(211, 47, 47, 0.035)",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

function StoreClosingTimeLine({
  timelineWidth,
  height,
}: {
  timelineWidth: number;
  height: number;
}) {
  const left = getStoreClosingLeftPx(timelineWidth);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: `${left}px`,
        height,
        width: "2px",
        bgcolor: "#d32f2f",
        zIndex: 20,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 2,
          transform: "translateX(-50%)",
          px: 0.75,
          py: 0.25,
          borderRadius: "999px",
          bgcolor: "#d32f2f",
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
        }}
      >
        ปิดร้าน 18:00
      </Box>
    </Box>
  );
}

function StoreClosingLegend() {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip
        label="เส้นแดง: ปิดร้าน 18:00"
        size="small"
        sx={{
          bgcolor: "#ffebee",
          color: "#b42318",
          border: "1px solid #ef9a9a",
          fontWeight: 700,
        }}
      />
      <Chip
        label="เสี่ยง: เวลาคาด 17:30-17:59"
        size="small"
        sx={{
          bgcolor: "#fff7e6",
          color: "#92400e",
          border: "1px solid #f59e0b",
          fontWeight: 700,
        }}
      />
      <Chip
        label="ตกแผน: เวลาคาด/จริงหลัง 18:00"
        size="small"
        sx={{
          bgcolor: "#fff1f2",
          color: "#b42318",
          border: "1px solid #d32f2f",
          fontWeight: 700,
        }}
      />
    </Stack>
  );
}

function getTimeSlotLeftPx(timeSlot: string, timelineWidth: number): string {
  const minutes = getMinutesFromTime(timeSlot) ?? TIMELINE_START_MINUTES;
  return `${getTimelineLeftPxForMinutes(minutes, timelineWidth).toFixed(0)}px`;
}

function TimelineRuler({ timelineWidth }: { timelineWidth: number }) {
  return (
    <Box
      height={32}
      width={getTimelineContentWidth(timelineWidth)}
      sx={{
        position: "relative",
        borderBottom: 1,
      }}
    >
      {timeSlots.map((timeSlot) => {
        const isHour = timeSlot.endsWith(":00");
        const isClosingTime = timeSlot === "18:00";

        return (
          <Box
            key={timeSlot}
            sx={{
              position: "absolute",
              left: getTimeSlotLeftPx(timeSlot, timelineWidth),
              top: 0,
              bottom: 0,
              width: 1,
              transform: "translateX(-0.5px)",
            }}
          >
            {isHour && (
              <Typography
                fontSize={12}
                align="center"
                sx={{
                  position: "absolute",
                  top: 0,
                  transform: "translateX(-50%)",
                  color: isClosingTime ? "#b42318" : "text.secondary",
                  fontWeight: isClosingTime ? 800 : 500,
                  whiteSpace: "nowrap",
                }}
              >
                {timeSlot}
              </Typography>
            )}
            <Box
              sx={{
                position: "absolute",
                bottom: 1,
                height: isHour ? "12px" : "7px",
                width: isClosingTime ? "2px" : "1px",
                bgcolor: isClosingTime ? "#d32f2f" : "#888",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function getPlanRiskCounts(routes: RouteGroup[]) {
  return routes.reduce(
    (counts, route) => {
      route.drops.forEach((drop) => {
        const risk = getDropPlanRisk(drop);
        if (risk?.level === "warning") counts.warning += 1;
        if (risk?.level === "late") counts.late += 1;
      });
      return counts;
    },
    { warning: 0, late: 0 }
  );
}

function formatQuantity(value: number): string {
  return (value || 0).toLocaleString("th-TH");
}

function formatMinutesAsTime(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function getRouteEtaRange(route: RouteGroup | null): string {
  if (!route || route.drops.length === 0) return "-";

  const etaMinutes = route.drops
    .map((drop) => getMinutesFromTime(drop.plannedTime))
    .filter((minutes): minutes is number => minutes !== null)
    .sort((a, b) => a - b);

  if (etaMinutes.length === 0) return "-";
  return `${formatMinutesAsTime(etaMinutes[0])} - ${formatMinutesAsTime(etaMinutes[etaMinutes.length - 1])}`;
}

function getRouteStatusSummary(route: RouteGroup | null): RouteStatusSummary[] {
  if (!route) return [];

  const summary = new Map<string, RouteStatusSummary>();
  route.drops.forEach((drop) => {
    const statusInfo = getDropStatusInfo(drop.status, drop.shippingType);
    const current = summary.get(statusInfo.label) || {
      label: statusInfo.label,
      count: 0,
      bgColor: statusInfo.cardBg || statusInfo.bgColor,
      textColor: statusInfo.textColor,
    };

    current.count += 1;
    summary.set(statusInfo.label, current);
  });

  return Array.from(summary.values()).sort((a, b) => b.count - a.count);
}

function getRouteRiskDrops(route: RouteGroup | null) {
  if (!route) return [];

  return route.drops
    .map((drop) => ({ drop, risk: getDropPlanRisk(drop) }))
    .filter((item): item is { drop: DropPoint; risk: DropPlanRisk } => !!item.risk)
    .sort((a, b) => {
      const levelScore = (risk: DropPlanRisk) => (risk.level === "late" ? 2 : 1);
      if (levelScore(a.risk) !== levelScore(b.risk)) {
        return levelScore(b.risk) - levelScore(a.risk);
      }
      return (
        (getMinutesFromTime(a.drop.effectiveTime) || 0) -
        (getMinutesFromTime(b.drop.effectiveTime) || 0)
      );
    });
}

function getRouteProductSummary(details: AdminDeliverySheetDetail[]): RouteProductSummary[] {
  const productMap = new Map<
    string,
    {
      productName: string;
      quantity: number;
      dropCodes: Set<string>;
    }
  >();

  details.forEach((detail) => {
    const dropCode = detail.order_code || detail.uuid;
    (detail.order_items || []).forEach((item) => {
      const productName = item.product_name || "ไม่ระบุสินค้า";
      const quantity = Number(item.quantity || 0);
      const current = productMap.get(productName) || {
        productName,
        quantity: 0,
        dropCodes: new Set<string>(),
      };

      current.quantity += quantity;
      current.dropCodes.add(dropCode);
      productMap.set(productName, current);
    });
  });

  return Array.from(productMap.values())
    .map((item) => ({
      productName: item.productName,
      quantity: item.quantity,
      dropCount: item.dropCodes.size,
    }))
    .sort((a, b) => b.quantity - a.quantity || b.dropCount - a.dropCount);
}

function getDeliveryPhotoTypeLabel(imageType?: string): string {
  return ({
    delivery_location: "ภาพจุดส่งสินค้า",
    recipient: "ภาพผู้รับสินค้า",
    temperature_display: "อุณหภูมิรถก่อนลงสินค้า",
    storefront: "ภาพหน้าร้าน",
    product_item: "ภาพสินค้า",
    expiry_label: "ภาพฉลากวันหมดอายุ",
    admin_evidence: "หลักฐานประกอบ",
    admin_adjustment: "การแก้ไขออเดอร์",
  } as Record<string, string>)[imageType || ""] || "รูปภาพหลักฐาน";
}

function getDeliveryPhotoFilename(photo: DeliveryPhoto, index: number, orderCode: string): string {
  return photo.original_filename || `${orderCode || "delivery-proof"}-${index + 1}.jpg`;
}

function DeliveryRouteSummaryDrawer({
  route,
  onClose,
}: {
  route: RouteGroup | null;
  onClose: () => void;
}) {
  const [routeDetails, setRouteDetails] = useState<AdminDeliverySheetDetail[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasDetailError, setHasDetailError] = useState(false);

  useEffect(() => {
    if (!route) {
      setRouteDetails([]);
      setHasDetailError(false);
      setIsLoadingDetails(false);
      return;
    }

    let isActive = true;
    setRouteDetails([]);
    setHasDetailError(false);
    setIsLoadingDetails(true);

    Promise.allSettled(
      route.drops.flatMap((drop) =>
        drop.detailTargets.map((target) =>
          APIGetAdminDeliverySheetDetail(target.uuid, target.orderUuid)
        )
      )
    )
      .then((results) => {
        if (!isActive) return;

        const fulfilledDetails = results
          .filter((result): result is PromiseFulfilledResult<AdminDeliverySheetDetail> => result.status === "fulfilled")
          .map((result) => result.value);

        setRouteDetails(fulfilledDetails);
        setHasDetailError(results.some((result) => result.status === "rejected"));
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [route]);

  const drops = route?.drops || [];
  const totalDrops = drops.length;
  const totalDetailTargets = drops.reduce(
    (sum, drop) => sum + drop.detailTargets.length,
    0
  );
  const etaRange = getRouteEtaRange(route);
  const statusSummary = useMemo(() => getRouteStatusSummary(route), [route]);
  const riskDrops = useMemo(() => getRouteRiskDrops(route), [route]);
  const productSummary = useMemo(() => getRouteProductSummary(routeDetails), [routeDetails]);
  const loadedDetailText =
    routeDetails.length === totalDetailTargets
      ? "โหลดรายละเอียดครบ"
      : `โหลดรายละเอียด ${routeDetails.length}/${totalDetailTargets} ออเดอร์`;

  return (
    <Drawer
      anchor="right"
      open={!!route}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 640 },
          p: 2,
        },
      }}
    >
      {route && (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box minWidth={0}>
              <Typography fontWeight={900} fontSize={20} noWrap>
                สรุปสายส่ง {route.route_code}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap" useFlexGap>
                <Chip label={`${totalDrops} Drop`} size="small" variant="outlined" />
                <Chip label={`แผน ${etaRange}`} size="small" variant="outlined" />
                {riskDrops.length > 0 && (
                  <Chip
                    label={`เสี่ยง/ตกแผน ${riskDrops.length}`}
                    size="small"
                    sx={{ bgcolor: "#fff1f2", color: "#b42318", fontWeight: 800 }}
                  />
                )}
              </Stack>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="ปิดสรุปสายส่ง">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocalShippingIcon fontSize="small" />
                <Typography fontWeight={800} noWrap>
                  {route.driver || "-"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon fontSize="small" />
                <Typography variant="body2">{route.phone || "-"}</Typography>
              </Stack>
            </Stack>
          </Paper>

          <Box>
            <Typography fontWeight={800} mb={1}>
              สถานะของสายส่ง
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {statusSummary.map((status) => (
                <Chip
                  key={status.label}
                  label={`${status.label} ${status.count}`}
                  size="small"
                  sx={{
                    bgcolor: status.bgColor,
                    color: status.textColor,
                    fontWeight: 800,
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography fontWeight={800}>สินค้าที่ต้องส่ง</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {isLoadingDetails && <CircularProgress size={16} />}
                <Chip
                  label={isLoadingDetails ? "กำลังโหลดรายละเอียด" : loadedDetailText}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Stack>
            {hasDetailError && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.75 }}>
                โหลดรายละเอียดบาง Drop ไม่สำเร็จ สรุปรายการสินค้าอาจยังไม่ครบ
              </Alert>
            )}
            {productSummary.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                {productSummary.slice(0, 12).map((item, index) => (
                  <Stack
                    key={item.productName}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderBottom:
                        index === Math.min(productSummary.length, 12) - 1 ? 0 : "1px solid #eeeeee",
                    }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap title={item.productName}>
                        {item.productName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        พบใน {item.dropCount} Drop
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Typography variant="body2" fontWeight={900} whiteSpace="nowrap">
                        {formatQuantity(item.quantity)}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
                {productSummary.length > 12 && (
                  <Box sx={{ px: 1.25, py: 0.75, bgcolor: "#fafafa" }}>
                    <Typography variant="caption" color="text.secondary">
                      และสินค้าอีก {productSummary.length - 12} รายการ
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                  color: "text.secondary",
                  bgcolor: "#fafafa",
                }}
              >
                {isLoadingDetails
                  ? "กำลังรวบรวมรายการสินค้าจาก Drop ในสายส่งนี้..."
                  : "ยังไม่มีรายละเอียดสินค้าในข้อมูลที่โหลดได้"}
              </Paper>
            )}
          </Box>

          {riskDrops.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <WarningAmberIcon fontSize="small" sx={{ color: "#b42318" }} />
                <Typography fontWeight={800}>Drop เสี่ยง/ตกแผน</Typography>
              </Stack>
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                {riskDrops.slice(0, 8).map(({ drop, risk }, index) => (
                  <Stack
                    key={drop.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      bgcolor: risk.level === "late" ? "#fff1f2" : "#fff7e6",
                      borderBottom: index === Math.min(riskDrops.length, 8) - 1 ? 0 : "1px solid #f2d6d6",
                    }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={800} noWrap title={drop.branchName}>
                        {drop.branchCode} • {drop.branchName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDropTimeSummary(drop)} • {risk.helperText}
                      </Typography>
                    </Box>
                    <Chip
                      label={risk.shortLabel}
                      size="small"
                      sx={{ bgcolor: risk.chipBg, color: risk.textColor, fontWeight: 800 }}
                    />
                  </Stack>
                ))}
              </Paper>
            </Box>
          )}

          <Box>
            <Typography fontWeight={800} mb={1}>
              รายการ Drop ทั้งหมด
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              {drops.map((drop, index) => {
                const statusInfo = getDropStatusInfo(drop.status, drop.shippingType);
                const risk = getDropPlanRisk(drop);
                return (
                  <Stack
                    key={drop.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderBottom: index === drops.length - 1 ? 0 : "1px solid #eeeeee",
                    }}
                  >
                    <Typography variant="caption" fontWeight={900} sx={{ width: 34 }}>
                      {drop.branchCode}
                    </Typography>
                    <Box minWidth={0} flex={1}>
                      <Typography variant="body2" fontWeight={700} noWrap title={drop.branchName}>
                        {drop.branchName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {getDropTimeSummary(drop)} • {drop.itemCount || 0} รายการ
                      </Typography>
                    </Box>
                    <Chip
                      label={risk?.shortLabel || statusInfo.shortLabel}
                      size="small"
                      sx={{
                        bgcolor: risk?.chipBg || statusInfo.bgColor,
                        color: risk?.textColor || statusInfo.textColor,
                        fontWeight: 800,
                      }}
                    />
                  </Stack>
                );
              })}
            </Paper>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}

function DeliveryDropDetailDrawer({
  drop,
  onClose,
}: {
  drop: DropPoint | null;
  onClose: () => void;
}) {
  const detailQueries = useQueries(
    (drop?.detailTargets || []).map((target) => ({
      queryKey: [
        EQueryKeys.GET_ADMIN_DELIVERY_SHEET_DETAIL,
        target.uuid,
        target.orderUuid,
      ],
      queryFn: () => APIGetAdminDeliverySheetDetail(target.uuid, target.orderUuid),
      enabled: !!drop,
      staleTime: 60000,
      cacheTime: 300000,
      retry: 1,
    }))
  ) as Array<{
    data?: AdminDeliverySheetDetail;
    isLoading: boolean;
    isError: boolean;
  }>;
  const details = detailQueries.flatMap((query) => (query.data ? [query.data] : []));
  const data = details[0];
  const isLoading = !!drop && detailQueries.some((query) => query.isLoading);
  const isError = detailQueries.some((query) => query.isError);
  const [previewImage, setPreviewImage] = useState<DeliveryPhotoPreviewState | null>(null);

  const statusInfo = drop ? getDropStatusInfo(drop.status, drop.shippingType) : null;
  const branchName = data?.branch_info?.branch_name || data?.branch_name || drop?.branchName || "-";
  const orderCodes = Array.from(
    new Set(details.map((detail) => detail.order_code).filter(Boolean))
  );
  const orderCode =
    orderCodes.length > 1
      ? `${orderCodes[0]} +${orderCodes.length - 1}`
      : orderCodes[0] || drop?.orderCode || "-";
  const routeCode = data?.route_code || drop?.routeCode || "-";
  const deliveryAddress = data?.delivery_address || drop?.deliveryAddress || "-";
  const invoiceNumbers = Array.from(
    new Set(
      details
        .map((detail) => detail.invoice_info?.invoice_number)
        .filter((value): value is string => !!value)
    )
  );
  const invoiceStatuses = Array.from(
    new Set(
      details
        .map((detail) => detail.invoice_info?.status)
        .filter((value): value is string => !!value)
    )
  );
  const receiverNames = Array.from(
    new Set(
      details
        .map((detail) => detail.delivery_info?.receiver_name)
        .filter((value): value is string => !!value)
    )
  );
  const receiverNotes = Array.from(
    new Set(
      details
        .map((detail) => detail.delivery_info?.receiver_note || detail.notes)
        .filter((value): value is string => !!value)
    )
  );
  const invoiceNumber = invoiceNumbers.join(", ") || drop?.invoiceNumber || "-";
  const invoiceStatus = invoiceStatuses.join(", ") || drop?.invoiceStatus || "-";
  const receiverName = receiverNames.join(", ") || "-";
  const receiverNote = receiverNotes.join("; ") || drop?.notes || "-";
  const orderItems = Array.from(
    details
      .flatMap((detail) => detail.order_items || [])
      .reduce((items, item) => {
        const key = item.product_name || "ไม่ระบุสินค้า";
        const existing = items.get(key) || { product_name: key, quantity: 0 };
        existing.quantity += Number(item.quantity || 0);
        items.set(key, existing);
        return items;
      }, new Map<string, { product_name: string; quantity: number }>())
      .values()
  );
  const photoEntries = details.flatMap((detail) =>
    [...(detail.delivery_info?.delivery_photos || [])].sort((a, b) => Number(b.image_type === 'temperature_display') - Number(a.image_type === 'temperature_display')).map((photo) => ({ photo, detail }))
  );
  const previewPhoto = previewImage?.photo;
  const previewOrderCode = previewImage?.detail.order_code || orderCode;
  const previewFilename = previewImage
    ? getDeliveryPhotoFilename(previewImage.photo, previewImage.index, previewOrderCode)
    : undefined;
  const previewDetails = useMemo<DeliveryImagePreviewDetail[]>(() => {
    if (!previewImage) return [];

    const photo = previewImage.photo;
    const detail = previewImage.detail;
    const previewInvoiceNumber = detail.invoice_info?.invoice_number;
    const previewReceiverName = detail.delivery_info?.receiver_name;
    return [
      { label: "Route", value: routeCode, mono: true },
      { label: "รหัสสาขา", value: drop?.branchCode },
      { label: "สาขา", value: branchName },
      { label: "ออเดอร์", value: detail.order_code, mono: true },
      { label: "Invoice", value: previewInvoiceNumber, mono: true },
      { label: "ผู้รับ", value: previewReceiverName },
      { label: "ประเภท", value: getDeliveryPhotoTypeLabel(photo.image_type) },
      { label: "ชื่อไฟล์", value: previewFilename, mono: true },
      {
        label: "อัปโหลดเมื่อ (เวลาไทย)",
        value: photo.created_at ? formatBangkokDateTime(photo.created_at, 'DD/MM/BBBB HH:mm:ss') : undefined,
      },
    ];
  }, [branchName, drop?.branchCode, previewFilename, previewImage, routeCode]);

  useEffect(() => {
    if (!drop) {
      setPreviewImage(null);
    }
  }, [drop]);

  const handleCloseDrawer = () => {
    setPreviewImage(null);
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={!!drop}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            p: 2,
          },
        }}
      >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box minWidth={0}>
          <Typography fontWeight={800} fontSize={18} noWrap>
            {branchName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {orderCode}
          </Typography>
        </Box>
        <IconButton onClick={handleCloseDrawer} size="small" aria-label="ปิดรายละเอียด">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {!drop ? null : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Route ${routeCode}`} size="small" variant="outlined" />
            <Chip label={`สาขา ${drop.branchCode}`} size="small" variant="outlined" />
            {drop.detailTargets.length > 1 && (
              <Chip
                label={`${drop.detailTargets.length} ออเดอร์`}
                size="small"
                variant="outlined"
              />
            )}
            {statusInfo && (
              <Chip
                label={statusInfo.label}
                size="small"
                sx={{
                  bgcolor: statusInfo.bgColor,
                  color: statusInfo.textColor,
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>

          <Grid container spacing={1.25}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                ETA ตามแผน
              </Typography>
              <Typography fontWeight={700}>{drop.plannedTime}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                ETA ล่าสุด
              </Typography>
              <Typography fontWeight={700}>{drop.latestTime}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                เวลาส่งจริง
              </Typography>
              <Typography fontWeight={700}>{drop.actualTime || "-"}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                ประเภท
              </Typography>
              <Typography fontWeight={700}>
                {drop.shippingType === "pickup" ? "รับของ" : "จัดส่ง"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                จำนวนรายการ
              </Typography>
              <Typography fontWeight={700}>{drop.itemCount || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                ยอดรวม
              </Typography>
              <Typography fontWeight={700}>
                {(drop.totalAmount || 0).toLocaleString("th-TH")} บาท
              </Typography>
            </Grid>
          </Grid>

          {isLoading && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">กำลังโหลดรายละเอียด...</Typography>
            </Stack>
          )}

          <Box>
            <Typography fontWeight={800} mb={1}>
              ข้อมูล Admin / EPOD
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  คนขับ: {data?.driver_name || drop.driver || "-"} ({data?.driver_phone || drop.driverPhone || "-"})
                </Typography>
                <Typography variant="body2">
                  ผู้รับ: {receiverName}
                </Typography>
                <Typography variant="body2">
                  เบอร์สาขา: {data?.branch_info?.phone || "-"}
                </Typography>
                <Typography variant="body2">
                  Invoice: {invoiceNumber} ({invoiceStatus})
                </Typography>
                <Typography variant="body2">
                  ที่อยู่: {deliveryAddress}
                </Typography>
                <Typography variant="body2">
                  หมายเหตุ: {receiverNote}
                </Typography>
              </Stack>
            </Paper>
          </Box>

          {isError && (
            <Alert severity="info" sx={{ py: 0.75 }}>
              รายละเอียด EPOD เพิ่มเติมยังไม่พร้อม แสดงข้อมูลจาก timeline และ invoice ที่มีอยู่ก่อน
            </Alert>
          )}

          {orderItems.length > 0 && (
            <Box>
              <Typography fontWeight={800} mb={1}>
                รายการสินค้า
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                {orderItems.slice(0, 5).map((item, index) => (
                  <Stack
                    key={`${item.product_name || "item"}-${index}`}
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderBottom: index === Math.min(orderItems.length, 5) - 1 ? 0 : "1px solid #eeeeee",
                    }}
                  >
                    <Typography variant="body2" noWrap title={item.product_name}>
                      {item.product_name || "-"}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
                      {item.quantity || 0}
                    </Typography>
                  </Stack>
                ))}
              </Paper>
            </Box>
          )}

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography fontWeight={800}>รูปภาพหลักฐาน</Typography>
              {photoEntries.length > 0 && (
                <Chip label={`${photoEntries.length} รูป`} size="small" color="success" variant="outlined" />
              )}
            </Stack>
            {photoEntries.length > 0 ? (
              <Grid container spacing={1}>
                {photoEntries.slice(0, 6).map(({ photo, detail }, index) => (
                  <Grid item xs={4} key={`${detail.order_uuid || detail.order_code}-${photo.uuid}`}>
                    <Tooltip title="ดูรูปภาพ">
                      <Box
                        component="button"
                        type="button"
                        aria-label={`ดูรูปภาพหลักฐาน ${index + 1}`}
                        onClick={() => setPreviewImage({ photo, index, detail })}
                        sx={{
                          p: 0,
                          width: "100%",
                          aspectRatio: "1 / 1",
                          display: "block",
                          overflow: "hidden",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          bgcolor: "transparent",
                          cursor: "zoom-in",
                          "&:hover img": {
                            transform: "scale(1.04)",
                          },
                          "&:focus-visible": {
                            outline: "2px solid #2e7d32",
                            outlineOffset: "2px",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={photo.s3_file_url}
                          alt={photo.original_filename || `รูปภาพหลักฐาน ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: "cover",
                            transition: "transform 160ms ease",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                  color: "text.secondary",
                  bgcolor: "#fafafa",
                }}
              >
                ยังไม่มีรูปจาก EPOD สำหรับ Drop นี้
              </Paper>
            )}
          </Box>
        </Stack>
      )}
      </Drawer>
      <DeliveryImagePreviewDialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewPhoto?.s3_file_url || ""}
        title={previewPhoto ? getDeliveryPhotoTypeLabel(previewPhoto.image_type) : "รูปภาพหลักฐาน"}
        filename={previewFilename}
        details={previewDetails}
        locationAudit={{
          location: getDeliveryPhotoLocation(previewPhoto?.capture_metadata),
          branch: {
            latitude: previewImage?.detail.coordinates?.latitude,
            longitude: previewImage?.detail.coordinates?.longitude,
            name: previewImage?.detail.branch_info?.branch_name || previewImage?.detail.branch_name,
            label: 'พิกัดจุดส่งตามใบงาน',
          },
        }}
      />
    </>
  );
}

export default function DeliverySchedule() {
  const timelineRef = useRef<HTMLDivElement>(null);
  const pendingTimelineCenterRatioRef = useRef<number | null>(null);
  const centeredDateRef = useRef<string>("");
  const [timelineZoom, setTimelineZoom] = useState(0.75);
  const [selectedDrop, setSelectedDrop] = useState<DropPoint | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<RouteGroup | null>(null);
  const timelineWidth = useMemo(
    () => Math.round(BASE_TIMELINE_WIDTH * timelineZoom),
    [timelineZoom]
  );
  const timelineRowHeight = useMemo(() => getTimelineRowHeight(timelineZoom), [timelineZoom]);
  
  // Form management
  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      keyword: "",
      delivery_date: dayjs(), // Default to today
    },
  });

  // Watch form values
  const deliveryDate = watch("delivery_date");
  const keyword = watch("keyword");

  // Query parameters for API
  const [queryParams, setQueryParams] = useState({
    delivery_date: dayjs().format("YYYY-MM-DD"),
    limit: 100, // Maximum allowed by API
    page: 1,
  });

  // State to collect all delivery data from all pages
  const [allDeliveryData, setAllDeliveryData] = useState<AdminDeliverySheetItem[]>([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);

  // API call
  const { data: deliveryData, isLoading, isError } = useGetAdminDeliverySheets(queryParams);

  // Fetch all pages when deliveryData changes
  useEffect(() => {
    const fetchAllPages = async () => {
      if (!deliveryData?.data || isFetchingAll) return;

      const pagination = deliveryData.pagination;
      if (!pagination) return;

      // If we're on page 1 and there are more pages, fetch them all
      if (queryParams.page === 1 && pagination.totalPages > 1) {
        setIsFetchingAll(true);
        
        try {
          // Collect all data including current page
          const allData: AdminDeliverySheetItem[] = [...deliveryData.data];

          // Fetch remaining pages (2 to totalPages)
          const promises = [];
          for (let page = 2; page <= pagination.totalPages; page++) {
            promises.push(
              multiFranchiseAxios.get<AdminDeliverySheetResponse>(
                `/procurement/order/admin/delivery-sheet`,
                {
                  params: {
                    delivery_date: queryParams.delivery_date,
                    limit: 100,
                    page: page,
                  },
                }
              )
            );
          }

          const results = await Promise.all(promises);
          results.forEach(result => {
            if (result.data?.status && result.data?.data) {
              allData.push(...result.data.data);
            }
          });

          console.log(`✅ โหลดข้อมูลครบทั้งหมด ${allData.length} รายการจาก ${pagination.totalPages} หน้า`);
          setAllDeliveryData(allData);
        } catch (error) {
          console.error("Error fetching all pages:", error);
          // If error, use only the first page data
          setAllDeliveryData(deliveryData.data);
        } finally {
          setIsFetchingAll(false);
        }
      } else {
        // Only one page, use it directly
        setAllDeliveryData(deliveryData.data);
      }
    };

    fetchAllPages();
  }, [deliveryData, queryParams.delivery_date, queryParams.page]);

  // Update query params when date changes
  useEffect(() => {
    if (deliveryDate) {
      setQueryParams({
        delivery_date: deliveryDate.format("YYYY-MM-DD"),
        limit: 100,
        page: 1, // Reset to page 1 when date changes
      });
      setAllDeliveryData([]); // Clear previous data
    }
  }, [deliveryDate]);

  // Transform API data to route groups
  const routes = useMemo<RouteGroup[]>(() => {
    if (!allDeliveryData || allDeliveryData.length === 0) return [];

    // Group by route_code
    const groupedByRoute = allDeliveryData.reduce((acc, item) => {
      const routeCode = item.route_code || "ไม่ระบุเส้นทาง";
      
      if (!acc[routeCode]) {
        acc[routeCode] = {
          route_code: routeCode,
          driver_name: item.driver_name,
          driver_phone: item.driver_phone,
          items: [],
        };
      }
      
      acc[routeCode].items.push(item);
      return acc;
    }, {} as Record<string, { route_code: string; driver_name: string; driver_phone: string; items: AdminDeliverySheetItem[] }>);

    // Convert to RouteGroup array
    return Object.entries(groupedByRoute)
      .map(([routeCode, routeData], index) => {
        // Sort items by index (ลำดับการส่ง) - ถ้ามี index ให้เรียงตาม index, ไม่มีให้เรียงตามลำดับที่ได้มา
        const sortedItems = [...routeData.items].sort((a, b) => {
          if (a.index !== undefined && b.index !== undefined) {
            return a.index - b.index;
          }
          return 0; // ถ้าไม่มี index ให้เรียงตามลำดับเดิม
        });
        const deliverySheetRows = groupDeliverySheetRows(sortedItems);

        return {
          id: routeCode,
          routeNo: index + 1,
          name: `#${index + 1}`,
          route_code: routeCode,
          driver: routeData.driver_name,
          phone: routeData.driver_phone,
          drops: deliverySheetRows.map((sheetRows, dropIndex) => {
            const item = sheetRows[0];
            const startTime = 6 * 60;
            const endTime = 18 * 60;
            const totalDrops = deliverySheetRows.length;
            const timeInterval = totalDrops > 1 ? (endTime - startTime) / (totalDrops - 1) : 0;
            const estimatedMinutes = startTime + timeInterval * dropIndex;
            const fallbackTime = `${String(Math.floor(estimatedMinutes / 60)).padStart(
              2,
              "0"
            )}:${String(Math.floor(estimatedMinutes % 60)).padStart(2, "0")}`;
            const timelineTimes = resolveDeliveryTimelineTimes({
              plannedEta: item.planned_eta,
              latestEta: item.latest_eta,
              actualDeliveryTime: getLatestDeliveryTimelineValue(
                sheetRows.map((row) => row.actual_delivery_time)
              ),
              legacyEta: item.eta,
              fallbackTime,
              status: item.status,
            });
            
            return {
              id: getDeliveryDropRenderId(
                item.uuid,
                item.order_uuid,
                item.order_code,
                dropIndex
              ),
              uuid: item.uuid,
              orderUuid: item.order_uuid,
              detailTargets: sheetRows.map((row) => ({
                uuid: row.uuid,
                orderUuid: row.order_uuid,
              })),
              routeCode: routeCode,
              driver: routeData.driver_name,
              driverPhone: routeData.driver_phone,
              plannedTime: timelineTimes.plannedTime,
              latestTime: timelineTimes.latestTime,
              actualTime: timelineTimes.actualTime,
              effectiveTime: timelineTimes.effectiveTime,
              comparisonLabel: timelineTimes.comparisonLabel,
              delayMinutes: timelineTimes.delayMinutes,
              dropNo: getOrderSuffix(item.order_code, `${dropIndex + 1}`),
              branchCode: getBranchCodeFromItem(item, `${dropIndex + 1}`),
              dropName: item.branch_name,
              branchName: item.branch_name,
              status: typeof item.status === "number" 
                ? (item.status === 2 ? "delivered" : item.status === 1 ? "assigned" : "pending")
                : item.status,
              orderCode: item.order_code,
              totalAmount: sheetRows.reduce(
                (sum, row) => sum + Number(row.total_amount || 0),
                0
              ),
              itemCount: sheetRows.reduce(
                (sum, row) => sum + Number(row.item_count || 0),
                0
              ),
              shippingType: item.shipping_type,
              deliveryAddress: item.delivery_address,
              invoiceNumber: item.invoice_info?.invoice_number,
              invoiceStatus: item.invoice_info?.status,
              notes: item.notes,
            };
          }),
        };
      })
      .filter((route) => {
        // Filter by keyword if provided
        if (!keyword) return true;
        const searchTerm = keyword.toLowerCase();
        return (
          route.route_code.toLowerCase().includes(searchTerm) ||
          route.driver.toLowerCase().includes(searchTerm) ||
          route.drops.some(drop => 
            drop.branchName.toLowerCase().includes(searchTerm) ||
            drop.orderCode.toLowerCase().includes(searchTerm)
          )
        );
      });
  }, [allDeliveryData, keyword]);
  const planRiskCounts = useMemo(() => getPlanRiskCounts(routes), [routes]);
  const timelineContentHeight = 32 + 8 + routes.length * timelineRowHeight;

  // เริ่มวันใหม่ที่ต้น timeline และรักษาตำแหน่งเดิมตอนซูม
  useEffect(() => {
    const container = timelineRef.current;
    if (!container || routes.length === 0) return;

    const pendingCenterRatio = pendingTimelineCenterRatioRef.current;
    if (pendingCenterRatio !== null) {
      container.scrollLeft = Math.max(
        0,
        pendingCenterRatio * timelineWidth - container.clientWidth / 2
      );
      pendingTimelineCenterRatioRef.current = null;
      return;
    }

    if (centeredDateRef.current !== queryParams.delivery_date) {
      container.scrollLeft = 0;
      centeredDateRef.current = queryParams.delivery_date;
    }
  }, [queryParams.delivery_date, routes.length, timelineWidth]);

  const rememberTimelineCenter = () => {
    const container = timelineRef.current;
    if (!container) return;
    pendingTimelineCenterRatioRef.current =
      (container.scrollLeft + container.clientWidth / 2) / timelineWidth;
  };

  const handleZoomIn = () => {
    rememberTimelineCenter();
    setTimelineZoom((current) => clampTimelineZoom(current + TIMELINE_ZOOM_STEP));
  };

  const handleZoomOut = () => {
    rememberTimelineCenter();
    setTimelineZoom((current) => clampTimelineZoom(current - TIMELINE_ZOOM_STEP));
  };

  const handleFitTimeline = () => {
    rememberTimelineCenter();
    const containerWidth = timelineRef.current?.clientWidth || BASE_TIMELINE_WIDTH;
    setTimelineZoom(clampTimelineZoom(containerWidth / BASE_TIMELINE_WIDTH));
  };

  return (
    <>
      <NavigationBar
        title="Delivery Schedule"
        sx={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}
        tools={
          <Stack direction="row" spacing={2} alignItems="center">
            <ButtonGroup size="small" variant="outlined" aria-label="timeline zoom controls">
              <Tooltip title="ซูมออก">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleZoomOut}
                    disabled={timelineZoom <= MIN_TIMELINE_ZOOM}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                onClick={handleFitTimeline}
                startIcon={<FitScreenIcon />}
                sx={{ minWidth: 88 }}
              >
                {Math.round(timelineZoom * 100)}%
              </Button>
              <Tooltip title="ซูมเข้า">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleZoomIn}
                    disabled={timelineZoom >= MAX_TIMELINE_ZOOM}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </ButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
              <Controller
                name="delivery_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="วันที่ส่ง"
                    inputFormat="DD/MM/YYYY"
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 200 }} />
                    )}
                  />
                )}
              />
            </LocalizationProvider>
            <SearchInput
              id="keyword"
              name="keyword"
              control={control}
              placeholder="ค้นหาเส้นทาง, คนขับ, สาขา"
              sx={{ width: 300 }}
            />
          </Stack>
        }
      />
      <Box sx={{ p: 2, backgroundColor: "#fff", height: "100vh" }}>
        {(isLoading || isFetchingAll) && (
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography>
                {isFetchingAll 
                  ? `กำลังโหลดข้อมูลทั้งหมด... (${allDeliveryData.length}+ รายการ)`
                  : "กำลังโหลดข้อมูล..."
                }
              </Typography>
            </Stack>
          </Box>
        )}
        
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง
          </Alert>
        )}

        {!isLoading && !isFetchingAll && !isError && routes.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            ไม่พบข้อมูลการจัดส่งในวันที่เลือก
          </Alert>
        )}

        {!isLoading && !isFetchingAll && !isError && routes.length > 0 && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1 }}
            >
              <StoreClosingLegend />
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`เสี่ยง ${planRiskCounts.warning}`}
                  size="small"
                  sx={{ bgcolor: "#fff7e6", color: "#92400e", fontWeight: 700 }}
                />
                <Chip
                  label={`ตกแผน ${planRiskCounts.late}`}
                  size="small"
                  sx={{ bgcolor: "#fff1f2", color: "#b42318", fontWeight: 700 }}
                />
              </Stack>
            </Stack>
          <Box sx={{ position: "relative", display: "flex" }}>
            {/* Routes Column */}
            <Grid
              container
              width={300}
              spacing={0}
              sx={{ padding: 0, borderRight: 1, background: "" }}
            >
              <Grid
                item
                xs={12}
                height={24}
                sx={{ borderBottom: 1, background: "" }}
              >
                <Typography fontWeight="bold">
                  Route ({routes.length} เส้นทาง, {allDeliveryData.length} รายการ)
                </Typography>
              </Grid>
              <Grid item xs={12} mt={1} sx={{ background: "", height: "100%" }}>
                {routes.map((route, i) => (
                  <Grid key={route.id} height={timelineRowHeight} sx={{ background: "" }}>
                    {/* Route Info */}
                    <Grid item xs={12} sx={{ background: "" }}>
                      <Paper
                        sx={{
                          px: 1,
                          py: timelineRowHeight <= 72 ? 0.75 : 1,
                          height: "100%",
                          background: "#fff",
                          borderRadius: 0,
                          boxShadow: "none",
                          borderBottom: "1px solid #e6e6e6",
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography fontWeight="bold" noWrap title={route.route_code} sx={{ minWidth: 0 }}>
                            {route.route_code}
                          </Typography>
                          <Tooltip title="ดูสรุปสายส่ง">
                            <IconButton
                              size="small"
                              aria-label={`ดูสรุปสายส่ง ${route.route_code}`}
                              onClick={() => setSelectedRoute(route)}
                              sx={{
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                                color: "#15803d",
                                border: "1px solid #d6eadf",
                                bgcolor: "#f0fdf4",
                                "&:hover": {
                                  bgcolor: "#dcfce7",
                                },
                              }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mt={timelineRowHeight <= 72 ? 0.5 : 1}
                        >
                          <LocalShippingIcon fontSize="small" />
                          <Typography variant="caption" noWrap>
                            {route.driver}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mt={timelineRowHeight <= 72 ? 0.25 : 1}
                        >
                          <PhoneIcon fontSize="small" />
                          <Typography variant="caption">{route.phone}</Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {/* Timeline */}
            <Grid
              item
              container
              xs={12}
              sx={{
                paddingBottom: "18px",
                overflowX: "scroll",
                overflowY: "hidden",
                position: "relative",
              }}
              ref={timelineRef}
            >
              <StoreClosingTimeLine timelineWidth={timelineWidth} height={timelineContentHeight} />
              <Grid item>
                <TimelineRuler timelineWidth={timelineWidth} />
              </Grid>

              {/* Drop cards */}
              <Grid item xs={12} mt={1}>
                {routes.map((route, i) => (
                  <Grid container key={route.id} height={timelineRowHeight}>
                    {/* Drop Area */}
                    <Grid item xs={10}>
                      <Box
                        sx={{
                          position: "relative",
                          borderBottom: "1px solid #eee",
                          width: getTimelineContentWidth(timelineWidth),
                          height: timelineRowHeight,
                        }}
                      >
                        <StoreClosingRiskZone timelineWidth={timelineWidth} />
                        {/* Cards */}
                        {route.drops.map((drop, idx) => (
                          <DropCard
                            key={drop.id}
                            drop={drop}
                            timelineWidth={timelineWidth}
                            timelineZoom={timelineZoom}
                            onSelect={setSelectedDrop}
                          />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Box>
          </Box>
        )}
      </Box>
      <DeliveryRouteSummaryDrawer route={selectedRoute} onClose={() => setSelectedRoute(null)} />
      <DeliveryDropDetailDrawer drop={selectedDrop} onClose={() => setSelectedDrop(null)} />
    </>
  );
}
